// Shared across cart mounts and AJAX replacements, without persisting ZIP codes.
const deliveryState = { method: "delivery", availability: "unknown" };
const visibilityEvent = "zapiet-cart:delivery-change";
const discoveryTimeout = 10_000;

export function applyDeliveryShippingVisibility(goal) {
  const wrapper = goal.closest("[data-delivery-shipping-goal]");
  if (wrapper) {
    wrapper.hidden = deliveryState.method === "pickup" || deliveryState.availability === "unavailable";
  }
}

function updateDeliveryState(changes) {
  if (Object.entries(changes).every(([key, value]) => deliveryState[key] === value)) return;
  Object.assign(deliveryState, changes);
  document.dispatchEvent(new Event(visibilityEvent));
}

/** Owns only the Zapiet mount's lifecycle; the provider owns its UI and checkout. */
class ZapietCart extends HTMLElement {
  #subscriptions;
  #apiSubscriptions;
  #fieldFrame;
  #startTimer;
  #startTimeout;
  #starting = false;
  #fieldObserver;
  #discoveryObserver;
  #discoveryTimer;
  #zipField;
  #zipDraft = null;

  connectedCallback() {
    this.#subscriptions = new AbortController();
    const options = { signal: this.#subscriptions.signal };
    document.addEventListener("zapiet:widget_loaded", this.#onWidgetLoaded, options);
    document.addEventListener("zapiet:widget_restored", this.#onSelectedMethod, options);
    document.addEventListener("zapiet:selected_method", this.#onSelectedMethod, options);
    document.addEventListener("zapiet:locationSelected", this.#onLocationSelected, options);
    this.addEventListener('input', this.#rememberZipDraft, options);
    this.refresh();
  }

  disconnectedCallback() {
    this.#subscriptions?.abort();
    this.#stopWaitingForApi();
    this.#stopDiscovery();
    this.#fieldObserver?.disconnect();
    this.#zipField = undefined;
    this.#fieldObserver = undefined;
    cancelAnimationFrame(this.#fieldFrame);
    this.#fieldFrame = undefined;
    clearTimeout(this.#startTimer);
    clearTimeout(this.#startTimeout);
    this.#startTimer = undefined;
    this.#starting = false;
  }

  // Called after the theme has finished morphing cart markup, never before it.
  refresh() {
    if (!this.isConnected) return;
    document.querySelectorAll("free-shipping-goal").forEach(applyDeliveryShippingVisibility);
    this.#trackZipField();
    const mount = this.querySelector("#storePickupApp");
    if (!mount || mount.childElementCount || this.#starting || this.#startTimer) return;
    this.#scheduleStart();
  }

  #apiReady() {
    return typeof window.Zapiet?.start === "function" && !!this.#widgetConfig()?.cached_config;
  }

  #widgetConfig() {
    // On an initially empty cart, 2.41.6 has not populated WidgetConfig yet.
    // Use the same loaded settings that Zapiet's own AJAX interceptor uses.
    return window.ZapietWidgetConfig?.cached_config ? window.ZapietWidgetConfig : window.ZapietCachedSettings;
  }

  #scheduleStart = () => {
    if (!this.isConnected || this.#starting || this.#startTimer) return;
    if (!this.#apiReady()) {
      this.#waitForApi();
      return;
    }
    this.#stopWaitingForApi();
    // Let Zapiet's automatic cart interceptor render first, then inspect again.
    this.#startTimer = setTimeout(() => {
      this.#startTimer = undefined;
      const mount = this.querySelector("#storePickupApp");
      if (!this.isConnected || !mount || mount.childElementCount || !this.#apiReady()) return;
      this.#starting = true;
      this.#startTimeout = setTimeout(() => { this.#starting = false; }, discoveryTimeout);
      try {
        window.Zapiet.start(this.#widgetConfig());
      } catch (error) {
        this.#starting = false;
        clearTimeout(this.#startTimeout);
        console.warn("Unable to initialize the cart delivery widget", error);
      }
    }, 300);
  };

  #waitForApi() {
    if (this.#apiSubscriptions) return;
    this.#apiSubscriptions = new AbortController();
    const options = { signal: this.#apiSubscriptions.signal };
    document.addEventListener("load", this.#onScriptLoaded, { ...options, capture: true });
    document.addEventListener("DOMContentLoaded", this.#scheduleStart, options);
    window.addEventListener("load", this.#scheduleStart, options);
    // Script load events also cover a slow provider. No timer or polling is needed.
  }

  #onScriptLoaded = (event) => {
    if (event.target instanceof HTMLScriptElement && this.#apiReady()) this.#scheduleStart();
  };

  #stopWaitingForApi() {
    this.#apiSubscriptions?.abort();
    this.#apiSubscriptions = undefined;
  }

  #onWidgetLoaded = () => {
    this.#starting = false;
    clearTimeout(this.#startTimer);
    clearTimeout(this.#startTimeout);
    this.#startTimer = undefined;
    this.#stopWaitingForApi();
    const method = window.Zapiet?.getSelectedCheckoutMethod?.();
    if (method === "delivery" || method === "pickup") updateDeliveryState({ method });
    this.#trackZipField();
  };

  #onSelectedMethod = (event) => {
    const method = event.detail?.method ?? event.detail;
    if (method !== "delivery" && method !== "pickup") return;
    updateDeliveryState({ method });
    if (method === "pickup") {
      this.#fieldObserver?.disconnect();
      this.#fieldObserver = undefined;
      this.#zipField = undefined;
      this.#stopDiscovery();
    }
    // selected_method can precede Zapiet's DOM update. Inspect after that render.
    if (!this.#fieldFrame) {
      this.#fieldFrame = requestAnimationFrame(() => {
        this.#fieldFrame = undefined;
        if (this.isConnected) this.#trackZipField();
      });
    }
  };

  #onLocationSelected = (event) => {
    if (event.detail?.method === "delivery" && event.detail.location) {
      updateDeliveryState({ availability: "available" });
    }
  };

  #rememberZipDraft = (event) => {
    if (event.target.id === 'deliveryGeoSearchField') this.#zipDraft = event.target.value;
  };

  #trackZipField() {
    if (deliveryState.method === "pickup") {
      this.#fieldObserver?.disconnect();
      this.#fieldObserver = undefined;
      this.#zipField = undefined;
      this.#stopDiscovery();
      return;
    }
    const mount = this.querySelector("#storePickupApp");
    const field = mount?.querySelector("#deliveryGeoSearchField");
    if (field && !field.value && this.#zipDraft !== null) field.value = this.#zipDraft;
    if (field === this.#zipField && field) return;
    this.#fieldObserver?.disconnect();
    this.#zipField = field;
    this.#fieldObserver = undefined;
    if (field) {
      this.#stopDiscovery();
      this.#fieldObserver = new MutationObserver(this.#onZipValidation);
      this.#fieldObserver.observe(field, { attributes: true, attributeFilter: ["aria-invalid"] });
      this.#onZipValidation();
    } else if (mount && deliveryState.method === "delivery" && !this.#discoveryObserver) {
      // Observe only while Zapiet asynchronously creates its ZIP field.
      this.#discoveryObserver = new MutationObserver(() => this.#trackZipField());
      this.#discoveryObserver.observe(mount, { childList: true, subtree: true });
      this.#discoveryTimer = setTimeout(() => this.#stopDiscovery(), discoveryTimeout);
    }
  }

  #onZipValidation = () => {
    if (deliveryState.method === "delivery" && this.#zipField?.value.trim() &&
        this.#zipField.getAttribute("aria-invalid") === "true") {
      updateDeliveryState({ availability: "unavailable" });
    }
    // aria-invalid=false also means editing/empty input, so it is NOT success.
    // Only locationSelected confirms that the delivery zone is available.
  };

  #stopDiscovery() {
    this.#discoveryObserver?.disconnect();
    this.#discoveryObserver = undefined;
    clearTimeout(this.#discoveryTimer);
  }
}

if (!customElements.get("zapiet-cart")) customElements.define("zapiet-cart", ZapietCart);
export { visibilityEvent as deliveryShippingVisibilityEvent };
